/**
 * "In context" (#306): /page#entity-<id> scrolls to where an item is mentioned
 * in an editor - a follow-up mention or an entity link - and flashes it.
 * The hash covers a fresh page load; REVEAL_EVENT covers a page that is
 * already open, where the editor does not remount.
 */
import { useEffect } from 'react';
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';

export const REVEAL_EVENT = 'dossier:reveal-entity';
const ID = /^[A-Za-z0-9_-]{1,128}$/;
const HASH = /^#entity-([A-Za-z0-9_-]{1,128})$/;

export function entityHash(id: string): string {
  return ID.test(id) ? `#entity-${id}` : '';
}

export function revealIdFromHash(hash: string): string | null {
  return HASH.exec(hash)?.[1] ?? null;
}

/** Position of the first node that points at `id`, or null. */
export function findEntityPos(doc: PMNode, id: string): number | null {
  let found: number | null = null;
  doc.descendants((node, pos) => {
    if (found !== null) return false;
    if ((node.type.name === 'mention' && node.attrs.followUpTaskId === id)
      || (node.type.name === 'entityLink' && node.attrs.entityId === id)) {
      found = pos;
      return false;
    }
    return true;
  });
  return found;
}

/** Asks any open editor to reveal `id`. */
export function requestReveal(id: string): void {
  if (ID.test(id)) window.dispatchEvent(new CustomEvent(REVEAL_EVENT, { detail: id }));
}

export const REVEAL_CLASS = 'entity-reveal';

export function useRevealEntity(editor: Editor | null): void {
  useEffect(() => {
    if (!editor) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const reveal = (id: string) => {
      if (editor.isDestroyed) return;
      const pos = findEntityPos(editor.state.doc, id);
      if (pos === null) return;
      const el = editor.view.nodeDOM(pos);
      if (!(el instanceof HTMLElement)) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add(REVEAL_CLASS);
      timers.push(setTimeout(() => el.classList.remove(REVEAL_CLASS), 2500));
    };
    const fromHash = revealIdFromHash(window.location.hash);
    if (fromHash) timers.push(setTimeout(() => reveal(fromHash), 300));
    const onEvent = (e: Event) => {
      const id = (e as CustomEvent).detail;
      if (typeof id === 'string' && ID.test(id)) reveal(id);
    };
    window.addEventListener(REVEAL_EVENT, onEvent);
    return () => {
      window.removeEventListener(REVEAL_EVENT, onEvent);
      timers.forEach(clearTimeout);
    };
  }, [editor]);
}
